import { valibotResolver } from "@hookform/resolvers/valibot";
import { useEffect, useLayoutEffect } from "react";
import { useForm } from "react-hook-form";

import useDebounce from "common/hooks/useDebounce";
import useInboxDraftStore from "common/hooks/useInboxDraftStore";

import useConversationUpdates from "../../../datahooks/useConversationUpdates";
import { InboxMessageSchema } from "../../../schemas";
import useFileUploadStore from "../../../stores/useFileUploadStore";
import MessageField from "./MessageField";

import type { Conversation, ConversationMessage } from "../../../types";

interface MessageContainerProps {
  conversation: Conversation;
  editMessage: ConversationMessage;
  hasMessageFromAnotherUser: boolean;
  cancelEdit: () => void;
}

export default function MessageContainer({
  conversation,
  editMessage,
  hasMessageFromAnotherUser,
  cancelEdit,
}: MessageContainerProps) {
  const { participants } = conversation || {};
  const formReturn = useForm<{ message: string }>({
    resolver: valibotResolver(InboxMessageSchema),
    defaultValues: {
      message: "",
    },
  });
  const { watch, handleSubmit, reset } = formReturn;
  const { sendMessage, isSendingMessage } = useConversationUpdates();
  const { drafts, setDraft, removeDraft } = useInboxDraftStore();

  const currentMessage = watch("message");
  const [debouncedMessage] = useDebounce(currentMessage, 300);

  const { conversation_urn: conversationUrn, owner: accountId } = conversation;
  const draftKey = `${conversationUrn}:${accountId}`;

  // Use file upload store
  const { getUploadedFiles, clearConversationFiles } = useFileUploadStore();
  const uploadedFiles = getUploadedFiles(draftKey);

  useLayoutEffect(() => {
    if (!editMessage || conversation) {
      reset({ message: drafts[draftKey] || "" });
    }
  }, [editMessage, conversation, reset, drafts, draftKey]);

  useEffect(() => {
    if (debouncedMessage) {
      setDraft(draftKey, debouncedMessage);
    } else if (debouncedMessage !== undefined) {
      removeDraft(draftKey);
    }
  }, [debouncedMessage]);

  // Clear files when conversation changes
  useEffect(() => {
    clearConversationFiles(draftKey);
  }, [conversationUrn, accountId, clearConversationFiles, draftKey]);

  function onSend({ message }: { message: string }) {
    if (isSendingMessage) {
      return;
    }

    // Early return if both message and uploadedFiles are empty
    if (!message.trim() && !uploadedFiles.length) {
      return;
    }

    reset({ message: "" });

    sendMessage({
      accountId,
      message,
      recipients: participants,
      uploadedFiles,
    });

    // Clear files after sending
    clearConversationFiles(draftKey);
  }

  return (
    <MessageField
      conversation={conversation}
      editMessage={editMessage}
      cancelEdit={cancelEdit}
      onSend={handleSubmit(onSend)}
      isSendingMessage={isSendingMessage}
      formReturn={formReturn}
      hasMessageFromAnotherUser={hasMessageFromAnotherUser}
    />
  );
}
